import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Navbar } from "../components/Navbar";
import api from "../lib/api";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const subscriptionId = searchParams.get("subscription_id");
  const plan = searchParams.get("plan");
  const [status, setStatus] = useState(subscriptionId ? "activating" : "success");

  useEffect(() => {
    if (!subscriptionId) return;

    const activate = async () => {
      try {
        const res = await api.post("/paypal/activate-subscription", {
          subscription_id: subscriptionId
        });
        if (res.data.status === "ACTIVE") {
          if (res.data.user) {
            localStorage.setItem("vita_user", JSON.stringify(res.data.user));
          }
          setStatus("success");
        } else {
          // Subscription might not be active yet, poll
          pollActivation(subscriptionId, 0);
        }
      } catch {
        pollActivation(subscriptionId, 0);
      }
    };

    const pollActivation = async (subId, attempt) => {
      if (attempt >= 5) {
        setStatus("pending");
        return;
      }
      await new Promise(r => setTimeout(r, 3000));
      try {
        const res = await api.post("/paypal/activate-subscription", {
          subscription_id: subId
        });
        if (res.data.status === "ACTIVE") {
          if (res.data.user) {
            localStorage.setItem("vita_user", JSON.stringify(res.data.user));
          }
          setStatus("success");
        } else {
          pollActivation(subId, attempt + 1);
        }
      } catch {
        pollActivation(subId, attempt + 1);
      }
    };

    activate();
  }, [subscriptionId]);

  const tierLabel = { premium: "Premium", super_premium: "Super Premium" };

  return (
    <div className="min-h-screen bg-white" data-testid="payment-success-page">
      <Navbar />
      <div className="max-w-lg mx-auto px-6 py-24 text-center">
        {status === "activating" && (
          <div className="animate-fade-in" data-testid="payment-checking">
            <Loader2 className="h-16 w-16 text-[#0B6E4F] animate-spin mx-auto mb-6" />
            <h2 className="font-['Outfit'] text-2xl font-bold text-[#0F172A] mb-2">Activating Subscription</h2>
            <p className="text-slate-500">Please wait while we confirm your subscription with PayPal...</p>
          </div>
        )}

        {status === "success" && (
          <div className="animate-fade-in-up" data-testid="payment-confirmed">
            <div className="w-20 h-20 bg-[#D4F4DD] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-[#0B6E4F]" />
            </div>
            <h2 className="font-['Outfit'] text-3xl font-bold text-[#0F172A] mb-3">Subscription Active!</h2>
            <p className="text-slate-500 mb-8">
              {plan ? `Your ${tierLabel[plan] || plan} subscription is now active.` : "Your subscription has been activated."} You'll be auto-billed monthly via PayPal.
            </p>
            <Link to="/dashboard">
              <Button data-testid="go-to-dashboard-btn" className="bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full px-8 py-3 font-semibold shadow-lg transition-all">
                Go to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        )}

        {status === "pending" && (
          <div className="animate-fade-in" data-testid="payment-pending">
            <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="h-10 w-10 text-amber-500" />
            </div>
            <h2 className="font-['Outfit'] text-2xl font-bold text-[#0F172A] mb-3">Subscription Pending</h2>
            <p className="text-slate-500 mb-8">
              Your subscription is being processed by PayPal. It may take a moment to activate. Check your dashboard for status updates.
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/pricing">
                <Button variant="outline" className="rounded-full px-6 border-slate-200">Back to Pricing</Button>
              </Link>
              <Link to="/dashboard">
                <Button className="bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full px-6">Dashboard</Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
